// [SUPPORT 2026-11] "Technical support" in a staff dashboard's header (and on
// the renewal screen of a locked school): write to the platform's super admin
// about a fault, an emergency or an account, and read their replies.
// Who is writing is NOT sent from here: the server reads it from the session.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { LifeBuoy, Loader2, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { supportApi } from '../services/apiService';
import { apiErrorMessage } from '../lib/apiError';
import useSubmitGuard from '../hooks/useSubmitGuard';

export const SUPPORT_CATEGORIES = ['technical', 'emergency', 'account', 'other'];

export function formatWhen(value, language) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const locale = language === 'ar' ? 'ar' : language === 'fr' ? 'fr-FR' : 'en-GB';
  return d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

export function StatusBadge({ status }) {
  const { t } = useTranslation();
  const tone = {
    open: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    in_progress: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
    resolved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  }[status] || 'bg-muted text-muted-foreground';
  return <span className={`text-xs rounded-full px-2 py-0.5 ${tone}`}>{t(`sup_status_${status}`)}</span>;
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={active}
      className={`text-sm rounded-full border px-3 py-1 ${active
        ? 'bg-teal-700 border-teal-700 text-white' : 'bg-card hover:bg-muted'}`}>
      {children}
    </button>
  );
}

export function Thread({ messages, language }) {
  const { t } = useTranslation();
  return (
    <ol className="space-y-2" data-testid="support-thread">
      {(messages || []).map((m) => (
        <li key={m.id} className={`rounded-lg p-2 text-sm ${m.author === 'support'
          ? 'bg-teal-50 dark:bg-teal-900/20' : 'bg-muted/50'}`}>
          <p className="text-xs text-muted-foreground mb-1">
            {m.author === 'support' ? t('sup_support_team') : (m.author_name || t('sup_sender'))}
            {' · '}{formatWhen(m.created_at, language)}
          </p>
          <p className="whitespace-pre-wrap break-words">{m.body}</p>
        </li>
      ))}
    </ol>
  );
}

function NewTicket({ user, page, onSent }) {
  const { t } = useTranslation();
  const [busy, guard] = useSubmitGuard();
  const [category, setCategory] = useState('technical');
  const [urgency, setUrgency] = useState('normal');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const submit = guard(async () => {
    if (subject.trim().length < 3 || message.trim().length < 10) {
      toast.error(t('sup_too_short'));
      return;
    }
    try {
      const ticket = await supportApi.createTicket({
        category, urgency, subject: subject.trim(), message: message.trim(),
        ...(page ? { context: { page: String(page).slice(0, 200) } } : {}),
      });
      toast.success(t('sup_sent', { number: ticket.number }));
      setSubject('');
      setMessage('');
      onSent(ticket);
    } catch (err) {
      toast.error(apiErrorMessage(err, t('sup_send_error')));
    }
  });

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="support-new">
      <div>
        <p className="text-sm font-medium mb-2">{t('sup_category')}</p>
        <div className="flex flex-wrap gap-2">
          {SUPPORT_CATEGORIES.map((c) => (
            <Chip key={c} active={category === c} onClick={() => setCategory(c)} testid={`sup-cat-${c}`}>
              {t(`sup_cat_${c}`)}
            </Chip>
          ))}
        </div>
      </div>
      <div>
        <p className="text-sm font-medium mb-2">{t('sup_urgency')}</p>
        <div className="flex gap-2">
          {['normal', 'urgent'].map((u) => (
            <Chip key={u} active={urgency === u} onClick={() => setUrgency(u)} testid={`sup-urg-${u}`}>
              {t(`sup_urgency_${u}`)}
            </Chip>
          ))}
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium" htmlFor="sup-subject">{t('sup_subject')}</label>
        <Input id="sup-subject" value={subject} maxLength={120} onChange={(e) => setSubject(e.target.value)}
          placeholder={t('sup_subject_ph')} data-testid="sup-subject" />
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium" htmlFor="sup-message">{t('sup_message')}</label>
        <Textarea id="sup-message" rows={5} value={message} maxLength={4000}
          onChange={(e) => setMessage(e.target.value)} placeholder={t('sup_message_ph')}
          data-testid="sup-message" />
      </div>
      <p className="text-xs text-muted-foreground rounded-lg border bg-muted/30 p-2">
        {t('sup_attached')}{user?.full_name ? ` (${user.full_name})` : ''}
      </p>
      <Button type="submit" disabled={busy} className="w-full" data-testid="sup-send">
        {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Send className="w-4 h-4 me-2" />}
        {busy ? t('sup_sending') : t('sup_send')}
      </Button>
    </form>
  );
}

function MyTickets({ locked, highlight }) {
  const { t, i18n } = useTranslation();
  const [busy, guard] = useSubmitGuard();
  const [tickets, setTickets] = useState(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(highlight || null);
  const [reply, setReply] = useState('');

  const load = useCallback(() => {
    setError('');
    supportApi.myTickets()
      .then((d) => setTickets(d.tickets || []))
      .catch((err) => setError(apiErrorMessage(err, t('sup_load_error'))));
  }, [t]);
  useEffect(load, [load]);

  const send = guard(async (ticketId) => {
    if (!reply.trim()) return;
    try {
      await supportApi.addMessage(ticketId, reply.trim());
      setReply('');
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('sup_send_error')));
    }
  });

  if (error) return <p className="text-sm text-red-700" role="alert">{error}</p>;
  if (!tickets) return <Loader2 className="w-5 h-5 animate-spin text-teal-600" />;
  if (!tickets.length) return <p className="text-sm text-muted-foreground">{t('sup_none')}</p>;
  return (
    <ul className="space-y-2" data-testid="support-mine">
      {tickets.map((tk) => (
        <li key={tk.id} className="rounded-lg border">
          <button type="button" className="w-full flex items-center justify-between gap-2 p-3 text-start"
            onClick={() => setOpenId(openId === tk.id ? null : tk.id)} data-testid="sup-mine-row">
            <span className="min-w-0">
              <span className="font-mono text-xs text-muted-foreground me-2">{tk.number}</span>
              <span className="font-medium break-words">{tk.subject}</span>
            </span>
            <StatusBadge status={tk.status} />
          </button>
          {openId === tk.id && (
            <div className="border-t p-3 space-y-3">
              <Thread messages={tk.messages} language={i18n.language} />
              {locked ? (
                <p className="text-xs text-muted-foreground">{t('sup_locked_note')}</p>
              ) : (
                <div className="flex gap-2">
                  <Textarea rows={2} value={reply} maxLength={4000} onChange={(e) => setReply(e.target.value)}
                    placeholder={t('sup_follow_up_ph')} data-testid="sup-follow-up" />
                  <Button type="button" disabled={busy || !reply.trim()} onClick={() => send(tk.id)}
                    data-testid="sup-follow-up-send">{t('sup_follow_up_send')}</Button>
                </div>
              )}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function SupportButton({ user, page, locked = false, className = '' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('new');
  const [lastSent, setLastSent] = useState(null);

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} className={className}
        title={t('sup_button')} aria-label={t('sup_button')} data-testid="support-button">
        <LifeBuoy className="w-4 h-4" />
        <span className="hidden sm:inline ms-1">{t('sup_button')}</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('sup_title')}</DialogTitle>
            <DialogDescription>{t('sup_intro')}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 border-b pb-2">
            <Chip active={view === 'new'} onClick={() => setView('new')} testid="sup-tab-new">{t('sup_tab_new')}</Chip>
            <Chip active={view === 'mine'} onClick={() => setView('mine')} testid="sup-tab-mine">{t('sup_tab_mine')}</Chip>
          </div>
          {view === 'new'
            ? <NewTicket user={user} page={page} onSent={(tk) => { setLastSent(tk.id); setView('mine'); }} />
            : <MyTickets locked={locked} highlight={lastSent} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
